import { Organization, PermissionUri, Thunk } from '@typings';
import * as services from '@services';
import {
  ConfigState,
  setConfig,
  setContext,
  setPermissions,
  setSystemRole,
  setUserClusters,
  setUserOrganizations,
  setUserTerms,
} from '@slices';

/**
 * Update context on config update when needed
 */
type GetConfigParams = {
  organizationName?: Extract<Organization.Name, string>;
  clusterName?: string;
};

export const getConfig =
  (options: GetConfigParams = {}): Thunk<Promise<ConfigState>> =>
  async (dispatch) => {
    try {
      const config = await services.getConfig();

      const { clusters = [], orgs: organizations = [] } = config;
      const { organizationName, clusterName } = options;

      const organization = organizations.find(
        ({ name }) => name === organizationName,
      );
      const cluster = clusters.find(({ name }) => name === clusterName);

      if (organization) {
        dispatch(setContext({ organization }));
      }

      if (cluster) {
        dispatch(setContext({ cluster }));
      }

      dispatch(setConfig(config));

      return config;
    } catch (error) {
      return Promise.reject(error);
    }
  };

export const getPermissions =
  (username: string, uri: PermissionUri): Thunk =>
  async (dispatch) => {
    try {
      const permissions = await services.getPermissions({ username, uri });

      dispatch(setPermissions(permissions));

      return permissions;
    } catch (error) {
      return Promise.reject(error);
    }
  };

export const getUser =
  (
    adminUrl: string,
    username: string,
  ): Thunk<ReturnType<typeof services.getUser>> =>
  async (dispatch) => {
    try {
      const [{ clusters, systemRole, termsAccepted = false }, { orgs }] =
        await Promise.all([
          services.getUser({
            adminUrl,
            username,
          }),
          services.getUserOrganizationsCredits({
            adminUrl,
            username,
          }),
        ]);

      dispatch(setUserClusters(clusters));
      dispatch(setUserOrganizations(orgs));
      dispatch(setUserTerms(termsAccepted));
      dispatch(setUserTerms(termsAccepted));

      if (systemRole) {
        dispatch(setSystemRole(systemRole));
      }

      return {
        orgs,
        clusters,
        systemRole,
        termsAccepted,
      };
    } catch (error) {
      return Promise.reject(error);
    }
  };
